import { Fragment, useState, useMemo, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import AnalyticsDetailsModal from './AnalyticsDetailsModal'
import LoadingSpinner from '../../../common/LoadingSpinner'
import SearchWithFilter from '../../../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../../../common/FiltersModal'
import EmptyPageResults from '../../../common/EmptyPageResults'
import Dropdown from '../../../common/Dropdown'
import GenericTable from '../../../common/GenericTable'
import BinaryToggle from '../../../common/BinaryToggle'
import CheckboxInput from '../../../common/CheckboxInput'
import SimpleButton from '../../../common/SimpleButton'
import WrappedTabs from '../../../common/WrappedTabs'
import { toast } from '../../../common/Toast'
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect'
import { downloadFile } from '../../../../utils/functions'
import countries from '../../../../utils/countries.json'
import { ExpandLessIcon, ExpandMoreIcon, InformationIcon } from '../../../../icons'
import {
  getAnalyticsFaultsOverview,
  getAnalyticsFaultsCountries,
  getAnalyticsFaultsSummary,
  getAnalyticsFaultsListCsv,
  getAnalyticsFaultsCodes,
} from '../../../../utils/requests/cyclesAPI'
import { loadAnalyticsFaultsList } from '../../../../actions/reports'
import CycleFaultSummaryPieChart from './CycleFaultSummaryPieChart'
import './cycle-analytics.scss'
import '../../internal-reports.scss'

const orderingFields = [
  {
    field: 'serialNumber',
    order: 'asc',
    translationKey: 'serial_number_asc',
  },
  {
    field: 'serialNumber',
    order: 'desc',
    translationKey: 'serial_number_desc',
  },
  {
    field: 'model',
    order: 'asc',
    translationKey: 'model_asc',
  },
  {
    field: 'model',
    order: 'desc',
    translationKey: 'model_desc',
  },
  {
    field: 'date',
    order: 'asc',
    translationKey: 'date_asc',
  },
  {
    field: 'date',
    order: 'desc',
    translationKey: 'date_desc',
  },
  {
    field: 'cycleFault',
    order: 'asc',
    translationKey: 'cycleFault_asc',
  },
  {
    field: 'cycleFault',
    order: 'desc',
    translationKey: 'cycleFault_desc',
  },
]

const CFAnalyticsReports = (props) => {
  const { t } = useTranslation()
  const [countryData, setCountryData] = useState([])
  const [analyticsListTableData, setAnalyticsListTableData] = useState([])
  const [allModels, setAllModels] = useState([])
  const [filterQuery, setFilterQuery] = useState(null)
  const [selectedFilterUnit, setSelectedFilterUnit] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [selectedFilterCountries, setSelectedFilterCountries] = useState([])
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [overviewTableData, setOverviewTableData] = useState([])
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(false)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [totalUnitsSold, setTotalUnitsSold] = useState(0)
  const [totalUnitsSoldWidth, setTotalUnitsSoldWidth] = useState(0)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedCfNumber, setSelectedCfNumber] = useState(-1)
  const [selectedDuration, setSelectedDuration] = useState(30)
  const [cfNumberOptions, setCfNumberOptions] = useState([-1])
  const [checkedModels, setCheckedModels] = useState({})
  const [selectedTab, setSelectedTab] = useState(0)
  const [summaryTableData, setSummaryTableData] = useState([])
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [isSerialNumberSearch, setIsSerialNumberSearch] = useState(false)
  const [areFiltersSelected, setAreFiltersSelected] = useState(false)
  const [analyticsDetailsCf, setAnalyticsDetailsCf] = useState(null)
  const [analyticsDetailsModel, setAnalyticsDetailsModel] = useState(null)
  const [analyticsDetailsCfDesc, setAnalyticsDetailsCfDesc] = useState(null)
  const [showAnalyticsDetailsModal, setShowAnalyticsDetailsModal] = useState(false)
  const [modelsTouched, setModelsTouched] = useState(false)
  const [pending, setPending] = useState(0)
  const showSpinner = pending > 0
  const getCountryName = (code) => countries.find((c) => c.code === code)?.name || code
  const countriesData = countryData.filter((code) => code !== 'ZZ').map(getCountryName)
  const isListTab = selectedTab === 1
  const lastSummaryKey = useRef(null)
  const track = (promise) => {
    setPending((n) => n + 1)
    return promise.finally(() => setPending((n) => Math.max(0, n - 1)))
  }

  const nameToCode = useMemo(() => {
    const m = new Map()
    countries.forEach(({ code, name }) => m.set(name, code))
    return m
  }, [countries])

  const selectedModels = useMemo(() => {
    if (isSerialNumberSearch) return null
    return Object.keys(checkedModels).filter((m) => checkedModels[m])
  }, [checkedModels, isSerialNumberSearch])

  const modelsForCheckboxes = useMemo(() => {
    if (allModels.length > 0) return allModels
    const set = new Set([...summaryTableData.map((m) => m.modelName), ...analyticsListTableData.map((r) => r.model)])
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [allModels, summaryTableData, analyticsListTableData])

  const allModelsSelected = useMemo(() => {
    if (isSerialNumberSearch) return false
    if (!selectedModels?.length) return false
    return selectedModels.length === modelsForCheckboxes.length
  }, [isSerialNumberSearch, selectedModels, modelsForCheckboxes])

  const modelsParam = useMemo(() => {
    if (isSerialNumberSearch) return null
    if (!modelsTouched) return null
    if (!selectedModels?.length) return null
    if (allModelsSelected) return null
    return selectedModels
  }, [isSerialNumberSearch, modelsTouched, selectedModels, allModelsSelected])

  const tabs = [
    {
      key: 0,
      label: 'monitoring.summary',
    },
    {
      key: 1,
      label: 'monitoring.list',
    },
  ]

  useEffect(() => {
    return () => {
      toast.dismiss()
    }
  }, [])

  useEffect(() => {
    window.addEventListener('resize', updateTotalSoldWidth)

    return () => {
      window.removeEventListener('resize', updateTotalSoldWidth)
    }
  }, [])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadCycleFaultsList(false)
    }
  }, [selectedPage])

  useEffect(() => {
    setCanLoadMore(isListTab && props.canLoadMore)
  }, [props.canLoadMore, isListTab])

  useEffect(() => {
    track(getAnalyticsFaultsCountries())
      .then((response) => {
        setCountryData(response.data)
      })
      .catch(() => {
        toast.error(t('error.something_wrong'))
      })
  }, [])

  useEffect(() => {
    if (selectedProduct) {
      let queryParams = { duration: selectedDuration }

      if (isSerialNumberSearch) {
        queryParams = { ...queryParams, sn: selectedProduct }
      } else {
        queryParams = { ...queryParams, product_type: selectedProduct }
      }

      track(getAnalyticsFaultsCodes(queryParams))
        .then((response) => {
          const codes = (response.data[0]?.codes || []).sort((a, b) => Number(a) - Number(b))
          setCfNumberOptions([-1, ...codes])
        })
        .catch(() => {
          toast.error(t('error.something_wrong'))
        })
    }
  }, [selectedProduct, selectedDuration, isSerialNumberSearch])

  useEffect(() => {
    const tableData = props.analyticsFaultListData.map((list, index) => ({
      index,
      sn: list.sn,
      model: list.model,
      date: list.date,
      fault: list.fault,
    }))

    setAnalyticsListTableData(tableData)
  }, [props.analyticsFaultListData])

  useDeepCompareEffect(() => {
    let queryParams = {}

    if (selectedFilterCountries.length > 0) {
      const countryCodes = selectedFilterCountries.map((c) => nameToCode.get(c)).filter(Boolean)
      queryParams = { ...queryParams, country: countryCodes }
    }

    if (shouldApplyFilters) {
      track(getAnalyticsFaultsOverview(queryParams))
        .then((response) => {
          const tableData =
            response?.data?.map((product) => ({
              productType: product.product_type,
              totalFaults: product.total_faults,
              totalSold: product.total_sold,
              uniqueSn: product.unique_sn,
            })) || []
          const totalUnitsSold = tableData.reduce((acc, product) => acc + product.totalSold, 0)

          setTotalUnitsSold(totalUnitsSold)
          setOverviewTableData(tableData)
          setIsOverviewExpanded(tableData.length > 0)

          updateTotalSoldWidth()
        })
        .catch(() => {
          toast.error(t('error.something_wrong'))
        })
    }
  }, [selectedFilterCountries, selectedFilterUnit, shouldApplyFilters])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (filterQuery && filterQuery.length >= 6 && filterQuery.length <= 13) {
          setIsSerialNumberSearch(true)
          setSelectedTab(0)
          setSummaryTableData([])
          setSelectedCfNumber(-1)
          setSelectedProduct(filterQuery)
        }
      },
      filterQuery ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [filterQuery])

  const summaryQueryParams = useMemo(() => {
    if (!selectedProduct) return null
    let qp = { duration: selectedDuration }
    if (selectedCfNumber !== -1) qp.cycle_fault = selectedCfNumber
    if (isSerialNumberSearch) qp.sn = selectedProduct
    else qp.product_type = selectedProduct

    if (selectedFilterCountries.length > 0) {
      const countryCodes = selectedFilterCountries
        .map((c) => nameToCode.get(c))
        .filter(Boolean)
        .sort()
      qp.country = countryCodes
    }

    if (modelsParam) qp.model = modelsParam
    Object.keys(qp).forEach((k) => {
      if (qp[k] === undefined || qp[k] === null || qp[k] === '' || (Array.isArray(qp[k]) && qp[k].length === 0)) {
        delete qp[k]
      }
    })
    return qp
  }, [selectedProduct, selectedDuration, selectedCfNumber, isSerialNumberSearch, selectedFilterCountries, nameToCode, modelsParam])

  const summaryKey = useMemo(() => (summaryQueryParams ? JSON.stringify(summaryQueryParams) : null), [summaryQueryParams])

  useEffect(() => {
    if (!summaryKey) return
    if (selectedTab !== 0) return
    if (lastSummaryKey.current === summaryKey) return
    lastSummaryKey.current = summaryKey
    const timer = setTimeout(() => {
      track(getAnalyticsFaultsSummary(summaryQueryParams))
        .then((response) => {
          const summaryData = response.data || []
          const groupedData = {}
          summaryData.forEach((item) => {
            const model = item.model
            if (!groupedData[model]) groupedData[model] = []
            groupedData[model].push(item)
          })
          const modelTables = Object.entries(groupedData).map(([modelName, modelData]) => {
            const tableData = modelData
              .flatMap((item) =>
                item.cycle_faults.map((fault, idx) => ({
                  index: idx,
                  identifier: `${modelName}-${idx}`,
                  model: modelName,
                  description: fault.description,
                  code: fault.code,
                  uniqueSn: fault.unique_sn,
                  totalCf: fault.total,
                })),
              )
              .slice(0, 10)
            return { modelName, tableData }
          })
          setSummaryTableData(modelTables)
          if (!selectedModels || selectedModels.length === 0) {
            const models = modelTables.map(({ modelName }) => modelName).sort((a, b) => a.localeCompare(b))
            setAllModels(models)
            const initialChecked = Object.fromEntries(models.map((m) => [m, true]))
            setCheckedModels(initialChecked)
          }
        })
        .catch(() => {
          toast.error(t('error.something_wrong'))
        })
    }, 120)
    return () => clearTimeout(timer)
  }, [summaryKey, selectedTab])

  useDeepCompareEffect(() => {
    if (!selectedProduct) return
    if (selectedTab !== 1) return
    setSelectedPage(1)
    handleLoadCycleFaultsList(true)
  }, [selectedProduct, isSerialNumberSearch, selectedTab, selectedCfNumber, selectedDuration, selectedFilterCountries, orderBy, checkedModels])

  useEffect(() => {
    if (isSerialNumberSearch) return
    if (modelsForCheckboxes.length === 0) return
    setCheckedModels((prev = {}) => {
      let changed = false
      const next = { ...prev }
      modelsForCheckboxes.forEach((m) => {
        if (!Object.prototype.hasOwnProperty.call(next, m)) {
          next[m] = true
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [modelsForCheckboxes, isSerialNumberSearch])

  const handleLoadCycleFaultsList = (withReset) => {
    let queryParams = { duration: selectedDuration }
    if (selectedCfNumber !== -1) {
      queryParams = { ...queryParams, cycle_fault: selectedCfNumber }
    }

    if (isSerialNumberSearch) {
      queryParams = { ...queryParams, sn: selectedProduct }
    } else {
      queryParams = { ...queryParams, product_type: selectedProduct }
    }

    if (selectedFilterCountries.length > 0) {
      const countryCodes = selectedFilterCountries.map((c) => nameToCode.get(c)).filter(Boolean)
      queryParams = { ...queryParams, country: countryCodes }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    if (modelsParam) {
      queryParams = { ...queryParams, model: modelsParam }
    }
    queryParams = { ...queryParams, page: withReset ? 1 : selectedPage }
    Object.keys(queryParams).forEach((k) => {
      if (queryParams[k] === undefined || queryParams[k] === null || queryParams[k] === '' || (Array.isArray(queryParams[k]) && queryParams[k].length === 0)) {
        delete queryParams[k]
      }
    })
    props.actions.loadAnalyticsFaultsList(queryParams, withReset)
  }
  const updateTotalSoldWidth = () => {
    const lastOverviewTableColumnWidth =
      document.querySelectorAll(`.overview-table .table tr:last-child td:last-child`)[0]?.getBoundingClientRect()?.width || 150

    setTotalUnitsSoldWidth(lastOverviewTableColumnWidth)
  }

  const handleSearch = (value) => {
    if (value?.length > 6) {
      setFilterQuery(value)
    } else if (value?.length === 0 && filterQuery?.length > 0) {
      setSelectedProduct(null)
      handleResetFilters()
    }
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleResetFilters = () => {
    setSelectedFilterUnit(null)
    setSelectedFilterCountries([])
    setAreFiltersSelected(false)
    setFilterQuery(null)
    setSelectedProduct(null)
    setIsSerialNumberSearch(false)
    setSelectedTab(0)
    setSelectedCfNumber(-1)
    setSelectedDuration(30)
    setCfNumberOptions([-1])
    setSelectedPage(1)
    setAllModels([])
    setCheckedModels({})
    setModelsTouched(false)
    lastSummaryKey.current = null
  }

  const selectCfNumber = (cfNumber) => {
    setSelectedCfNumber(cfNumber)
  }

  const selectDuration = (duration) => {
    setSelectedCfNumber(-1)
    setSelectedDuration(duration)
  }

  const handleSelectAll = () => {
    const allChecked = modelsForCheckboxes.every((m) => !!checkedModels[m])
    const updated = {}
    modelsForCheckboxes.forEach((m) => {
      updated[m] = !allChecked
    })
    setCheckedModels(updated)
    setModelsTouched(true)
  }

  const toggleModelCheckbox = (modelName) => {
    const updatedCheckedModels = { ...checkedModels, [modelName]: !checkedModels[modelName] }

    setCheckedModels(updatedCheckedModels)
    setModelsTouched(true)
  }

  const handleTabChange = (tabLabel) => {
    setSelectedTab(tabLabel)
    if (tabLabel !== 1) {
      setCanLoadMore(false)
    }
  }
  const handleOpenAnalyticsDetails = (item) => {
    setAnalyticsDetailsCf(item.code)
    setAnalyticsDetailsCfDesc(item.description)
    setAnalyticsDetailsModel(item.model)
    setShowAnalyticsDetailsModal(true)
  }

  const handleCloseAnalyticsDetails = () => {
    setAnalyticsDetailsCf(null)
    setAnalyticsDetailsCfDesc(null)
    setAnalyticsDetailsModel(null)
    setShowAnalyticsDetailsModal(false)
  }

  const handleSelectProduct = (productType, { setAsFilter = false, clearProductFilter: shouldClear = true } = {}) => {
    setSelectedCfNumber(-1)
    setCfNumberOptions([-1])
    setSelectedProduct(productType)
    setIsSerialNumberSearch(false)
    setSelectedTab(0)
    setSelectedPage(1)
    setAllModels([])
    setCheckedModels({})
    setModelsTouched(false)
    lastSummaryKey.current = null
    if (setAsFilter) {
      setSelectedFilterUnit(productType)
      setAreFiltersSelected(true)
      setShouldApplyFilters(false)
      return
    }
    if (shouldClear) {
      clearProductFilter()
    }
  }

  const clearProductFilter = () => {
    setShouldApplyFilters(false)
    setSelectedFilterUnit(null)
    setAreFiltersSelected(selectedFilterCountries.length > 0)
    setTimeout(() => setShouldApplyFilters(true), 0)
  }

  const renderCycleSummary = () => {
    return (
      <div className="grid-container">
        {summaryTableData.map(
          (modelTable) =>
            checkedModels[modelTable.modelName] && (
              <div key={modelTable.modelName} className="summary-model-block">
                <div className="summary-header">
                  {modelTable.modelName} ({t('reports.top_10_cf')})
                </div>
                <div className="summary-table-chart-row">
                  <div className="summary-table-wrapper">
                    <GenericTable
                      className="summary-table"
                      data={modelTable.tableData}
                      headers={[{ title: t('cf') }, { title: t('monitoring.total_cf') }, { title: t('monitoring.unique_sn_cf') }]}
                      keys={['code', 'totalCf', 'uniqueSn']}
                      onRowClick={(item) => handleOpenAnalyticsDetails(item)}
                      rowClassName={(item) => (item.code === analyticsDetailsCf ? 'row-selected' : '')}
                      keyRenderer={{
                        code: (item) => <div className="product-type-wrapper">{item.code}</div>,
                      }}
                    />
                  </div>
                  <div className="summary-chart-wrapper">
                    <CycleFaultSummaryPieChart data={modelTable.tableData} />
                  </div>
                </div>
              </div>
            ),
        )}
      </div>
    )
  }

  const renderCycleList = () => {
    return (
      <Fragment>
        <div className="list-download-wrapper">
          <div className="list-download d-flex">
            <SimpleButton className="action-button" disabled={showSpinner || analyticsListTableData.length === 0} onClick={() => exportToCsv()}>
              {t('download')}
            </SimpleButton>
          </div>
        </div>
        <div className="list-container">
          {analyticsListTableData.length > 0 && (
            <GenericTable
              data={analyticsListTableData}
              headers={[
                { title: t('date'), orderKey: 'date' },
                { title: t('serial_number'), orderKey: 'serialNumber' },
                { title: t('model'), orderKey: 'model' },
                { title: t('cycle_fault'), orderKey: 'cycleFault' },
              ]}
              keys={['date', 'sn', 'model', 'fault']}
              isLoading={props.isLoading}
              onSort={(orderingField) => handleSort(orderingField)}
              activeSort={orderBy}
            />
          )}
        </div>
      </Fragment>
    )
  }

  const exportToCsv = () => {
    let queryParams = { duration: selectedDuration }
    if (selectedCfNumber !== -1) queryParams = { ...queryParams, cycle_fault: selectedCfNumber }
    if (isSerialNumberSearch) queryParams = { ...queryParams, sn: selectedProduct }
    else queryParams = { ...queryParams, product_type: selectedProduct }
    if (selectedFilterCountries.length > 0) {
      const countryCodes = selectedFilterCountries.map((c) => nameToCode.get(c)).filter(Boolean)
      queryParams = { ...queryParams, country: countryCodes }
    }
    if (modelsParam) queryParams = { ...queryParams, model: modelsParam }
    track(getAnalyticsFaultsListCsv(queryParams))
      .then((res) => {
        const date = new Date().toISOString().split('T')
        const dateNow = date[0].split('-').reverse().join('_')
        const timeNow = date[1].split('.')[0].split(':').join('_')

        downloadFile({
          data: res,
          fileName: `analytics_${dateNow}_${timeNow}.csv`,
          fileType: 'text/csv',
        })
        toast.success(t('monitoring.download_success'))
      })
      .catch(() => {
        toast.error(t('error.failure_msg'))
      })
  }

  return (
    <div className="cycle-analytics-content-wrapper">
      <div className="actions-wrapper">
        <SearchWithFilter
          placeholder={t('device_context.search_by_serial_number')}
          onSearch={(e) => handleSearch(e)}
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={selectedFilterUnit || selectedFilterCountries.length > 0}
          counterBadge={(selectedFilterUnit ? 1 : 0) + selectedFilterCountries.length}
          className={`${areFiltersSelected ? 'disable-search-bar ' : ''} ${isSerialNumberSearch ? 'disabled-filters-button' : 'no-reset-button'}`}
          searchText={filterQuery}
        />
      </div>
      {!isSerialNumberSearch && (
        <div className="overview-expand-wrapper">
          <div className="overview-expand-header" onClick={() => setIsOverviewExpanded((prev) => !prev)}>
            <div className="title">
              {t('monitoring.overview')} - {t('monitoring.last_thirty_days')}
            </div>
            <div className="icon">{isOverviewExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
          </div>
          {isOverviewExpanded && (
            <div className="overview-expand-body">
              <div className="separator" />
              {overviewTableData.length > 0 && (
                <>
                  <div className="disclaimer-box-text">
                    <div className="icon-wrapper">
                      <InformationIcon />
                    </div>
                    <div className="disclaimer-wrapper">
                      <div className="title">{t('monitoring.click_product_disclaimer')}</div>
                    </div>
                  </div>
                  <GenericTable
                    className="overview-table"
                    data={overviewTableData}
                    headers={[
                      { title: t('product') },
                      { title: t('monitoring.unique_sn_cf') },
                      { title: t('monitoring.total_cf') },
                      { title: t('monitoring.units_in_field') },
                    ]}
                    keys={['productType', 'uniqueSn', 'totalFaults', 'totalSold']}
                    keyRenderer={{
                      productType: (item) => {
                        return <div className={'product-type-wrapper' + (item.productType === selectedProduct ? ' bold-font' : '')}>{item.productType}</div>
                      },
                    }}
                    onRowClick={(item) => handleSelectProduct(item.productType, { setAsFilter: true })}
                    rowClassName={(item) => (item.productType === selectedProduct ? 'row-selected' : '')}
                  />
                  <div className="total-units-wrapper">
                    <div className="label">{t('monitoring.total_units')}</div>
                    <div className="value" style={{ width: totalUnitsSoldWidth }}>
                      {totalUnitsSold}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      <div className="product-details-wrapper">
        <div className="details-header">
          {selectedProduct && <div className="product-name">{selectedProduct}</div>}
          <div className="filter-options-wrapper">
            <div className="filter-option toggle-filter">
              <div className="input-title">{t('business-rules.time_period')}:</div>
              <BinaryToggle
                ariaLabel={t('business-rules.time_period')}
                options={[
                  { label: t('monitoring.thirty_days') || '30 Days', value: 30 },
                  { label: t('filter_context.6_months') || '6 Months', value: 180 },
                ]}
                value={selectedDuration}
                onChange={(next) => selectDuration(next)}
              />
            </div>
            <div className="filter-option">
              <div className="input-title">{t('monitoring.cf_number')}:</div>
              <Dropdown
                className="dropdown-input"
                placeholder={t('monitoring.all_cf_selected')}
                options={cfNumberOptions?.map((cfNumber) => ({ key: cfNumber, value: cfNumber === -1 ? t('monitoring.all_cf_selected') : cfNumber }))}
                initialSelectedKey={selectedCfNumber}
                onChange={(cfNumber) => selectCfNumber(cfNumber.key)}
                isDisabled={!selectedProduct}
              />
            </div>
          </div>
        </div>
        {selectedProduct && (
          <>
            {!isSerialNumberSearch && (
              <div className="models-container">
                <div className="title">{t('monitoring.models')}</div>
                <div className="models-checkboxes">
                  <div className="models-checkbox" onClick={handleSelectAll}>
                    <CheckboxInput checked={modelsForCheckboxes.every((m) => !!checkedModels[m])} />
                    <span>{t('monitoring.all_models')}</span>
                  </div>
                  {modelsForCheckboxes.map((modelName) => (
                    <div key={modelName} className="models-checkbox">
                      <CheckboxInput checked={!!checkedModels[modelName]} onChange={() => toggleModelCheckbox(modelName)} />
                      {modelName}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <WrappedTabs tabs={tabs.map((tab) => ({ id: tab.key, name: tab.label }))} activeTab={selectedTab} changeTab={handleTabChange} />
            {selectedTab === 0 && renderCycleSummary()}
            {selectedTab === 1 && renderCycleList()}
          </>
        )}
        {!isListTab && selectedProduct && !showSpinner && summaryTableData.length === 0 && (
          <div className="no-product-wrapper">
            <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
          </div>
        )}
        {isListTab && selectedProduct && !showSpinner && analyticsListTableData.length === 0 && (
          <div className="no-product-wrapper">
            <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
          </div>
        )}
        {!selectedProduct && (
          <div className="no-product-wrapper">
            <EmptyPageResults title={t('no_product_selected')} subtitle={t('monitoring.no_product_selected_subtitle')} />
          </div>
        )}
        {isListTab && canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('nav.products'),
              type: FilterType.singleSelect,
              dataset: overviewTableData.map((item) => item.productType),
              input: selectedFilterUnit,
              output: (filterUnit) => {
                setSelectedFilterUnit(filterUnit)
                setAreFiltersSelected(true)
                setShouldApplyFilters(false)
                handleSelectProduct(filterUnit, { clearProductFilter: false })
                setTimeout(() => setShouldApplyFilters(true), 0)
              },
            },
            {
              name: t('country'),
              type: FilterType.multiSelect,
              dataset: countriesData,
              input: selectedFilterCountries,
              output: (filterCountries) => {
                setSelectedFilterCountries(filterCountries)
                setAreFiltersSelected(true)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showAnalyticsDetailsModal && (
        <AnalyticsDetailsModal
          model={analyticsDetailsModel}
          description={analyticsDetailsCfDesc}
          cf={analyticsDetailsCf}
          duration={selectedDuration}
          isSerialNumberSearch={isSerialNumberSearch}
          selectedProduct={selectedProduct}
          closeModal={handleCloseAnalyticsDetails}
        />
      )}
      {(showSpinner || props.isLoading) && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ reports }) {
  return {
    analyticsFaultListData: reports?.analyticsFaultListData || [],
    canLoadMore: reports?.canLoadMoreListData,
    isLoading: reports?.isLoading,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadAnalyticsFaultsList,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(CFAnalyticsReports)

